import { Pill, Package, Truck, CreditCard, RotateCcw } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    documentSubtype: 'ORDER_INVOICE' | 'PAYMENT_RECEIPT' | 'FULFILLMENT_RECEIPT' | 'DELIVERY_RECEIPT' | 'RETURN_REFUND' | 'BATCH_EXPIRY_SHEET';
    orderId: string;
    orderDate: string;
    prescriptionId?: string;
    pharmacyName: string;
    pharmacyNhcId?: string;
    pharmacistName?: string;
    pharmacistId?: string;
    deliveryMode?: string;
    deliveryAddress?: string;
    deliveryDate?: string;
    trackingId?: string;
    medicines: {
      name: string;
      dosage?: string;
      quantity: number;
      batchNo?: string;
      expiryDate?: string;
      mrp: number;
      sellingPrice: number;
    }[];
    subtotal: number;
    discount?: number;
    deliveryCharge?: number;
    gst?: number;
    total: number;
    amountPaid: number;
    balanceDue?: number;
    paymentMode?: string;
    paymentStatus: string;
    transactionId?: string;
    refundAmount?: number;
    refundReason?: string;
    notes?: string[];
    signatories: { name: string; role: string; nhcId?: string; licenseNo?: string; institution?: string }[];
  };
}

function fmt(n: number) { return `Rs. ${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`; }

const SUBTYPE_LABELS: Record<string, string> = {
  ORDER_INVOICE: 'Pharmacy Order Invoice',
  PAYMENT_RECEIPT: 'Pharmacy Payment Receipt',
  FULFILLMENT_RECEIPT: 'Prescription Fulfillment Receipt',
  DELIVERY_RECEIPT: 'Medicine Delivery Receipt',
  RETURN_REFUND: 'Medicine Return/Refund Receipt',
  BATCH_EXPIRY_SHEET: 'Medicine Batch/Expiry Transparency Sheet',
};

export default function PharmacyDocumentTemplate({ data }: Props) {
  const isRefund = data.documentSubtype === 'RETURN_REFUND';
  const isBatchSheet = data.documentSubtype === 'BATCH_EXPIRY_SHEET';

  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-1.5 mb-1">
        {isRefund ? <RotateCcw className="w-3.5 h-3.5 text-gray-700" /> : <Pill className="w-3.5 h-3.5 text-gray-700" />}
        <span className="text-[9px] font-bold uppercase tracking-wider text-gray-700">
          {SUBTYPE_LABELS[data.documentSubtype] || 'Pharmacy Document'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[9px]">
        <div><span className="text-gray-500">Order ID:</span> <span className="font-mono text-gray-800">{data.orderId}</span></div>
        <div><span className="text-gray-500">Date:</span> <span className="text-gray-800">{data.orderDate}</span></div>
        {data.prescriptionId && <div><span className="text-gray-500">Prescription:</span> <span className="font-mono text-gray-800">{data.prescriptionId}</span></div>}
        <div><span className="text-gray-500">Pharmacy:</span> <span className="text-gray-800">{data.pharmacyName}</span></div>
        {data.pharmacyNhcId && <div><span className="text-gray-500">Pharmacy NHC:</span> <span className="font-mono text-gray-700">{data.pharmacyNhcId}</span></div>}
        {data.pharmacistName && <div><span className="text-gray-500">Pharmacist:</span> <span className="text-gray-800">{data.pharmacistName}</span></div>}
      </div>

      {/* Medicine Table */}
      <div>
        <p className="text-[8px] font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1">
          <Package className="w-3 h-3" /> Medicines
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[9px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">Medicine</th>
                <th className="text-center font-bold text-gray-700 py-1 px-1.5 border border-gray-300 w-10">Qty</th>
                {(isBatchSheet || data.medicines.some(m => m.batchNo)) && (
                  <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">Batch</th>
                )}
                {(isBatchSheet || data.medicines.some(m => m.expiryDate)) && (
                  <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">Expiry</th>
                )}
                <th className="text-right font-bold text-gray-700 py-1 px-1.5 border border-gray-300">MRP</th>
                <th className="text-right font-bold text-gray-700 py-1 px-1.5 border border-gray-300">Price</th>
              </tr>
            </thead>
            <tbody>
              {data.medicines.map((m, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-800">
                    <span className="font-medium">{m.name}</span>
                    {m.dosage && <span className="text-gray-500 ml-1 text-[8px]">({m.dosage})</span>}
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-800 text-center">{m.quantity}</td>
                  {(isBatchSheet || data.medicines.some(x => x.batchNo)) && (
                    <td className="py-1 px-1.5 border border-gray-200 text-gray-600 font-mono text-[8px]">{m.batchNo || '—'}</td>
                  )}
                  {(isBatchSheet || data.medicines.some(x => x.expiryDate)) && (
                    <td className="py-1 px-1.5 border border-gray-200 text-gray-600 text-[8px]">{m.expiryDate || '—'}</td>
                  )}
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-500 text-right font-mono">{fmt(m.mrp)}</td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-800 text-right font-mono font-medium">{fmt(m.sellingPrice * m.quantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals */}
      {!isBatchSheet && (
        <div className="flex justify-end">
          <div className="w-56 bg-gray-50 border border-gray-200 rounded text-[9px]">
            <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Subtotal</span><span className="font-mono text-gray-800">{fmt(data.subtotal)}</span></div>
            {data.discount !== undefined && data.discount > 0 && (
              <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Discount</span><span className="font-mono text-green-700">- {fmt(data.discount)}</span></div>
            )}
            {data.deliveryCharge !== undefined && data.deliveryCharge > 0 && (
              <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Delivery</span><span className="font-mono text-gray-800">{fmt(data.deliveryCharge)}</span></div>
            )}
            {data.gst !== undefined && data.gst > 0 && (
              <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">GST</span><span className="font-mono text-gray-800">{fmt(data.gst)}</span></div>
            )}
            <div className="flex justify-between py-1 px-2.5 bg-gray-100 font-bold border-b border-gray-300"><span className="text-gray-800">Total</span><span className="font-mono text-gray-900">{fmt(data.total)}</span></div>
            <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Paid</span><span className="font-mono text-green-700">{fmt(data.amountPaid)}</span></div>
            {(data.balanceDue ?? 0) > 0 && (
              <div className="flex justify-between py-0.5 px-2.5 font-bold"><span className="text-gray-700">Due</span><span className="font-mono text-red-700">{fmt(data.balanceDue!)}</span></div>
            )}
          </div>
        </div>
      )}

      {isRefund && data.refundAmount !== undefined && (
        <div className="bg-amber-50 border border-amber-200 rounded px-3 py-1.5 text-[9px]">
          <p className="font-bold text-amber-800">Refund: {fmt(data.refundAmount)}</p>
          {data.refundReason && <p className="text-amber-700 mt-0.5">Reason: {data.refundReason}</p>}
        </div>
      )}

      {data.deliveryMode && (
        <div className="bg-gray-50 px-3 py-1.5 border border-gray-200 rounded text-[9px] flex items-center gap-1.5">
          <Truck className="w-3 h-3 text-gray-500" />
          <span className="text-gray-500">Delivery:</span>
          <span className="text-gray-800 capitalize">{data.deliveryMode.toLowerCase()}</span>
          {data.deliveryDate && <><span className="text-gray-400">·</span><span className="text-gray-800">{data.deliveryDate}</span></>}
          {data.trackingId && <><span className="text-gray-400">·</span><span className="font-mono text-gray-600">{data.trackingId}</span></>}
        </div>
      )}

      {data.notes && data.notes.length > 0 && (
        <ul className="list-disc list-inside space-y-0.5 text-[9px] text-gray-700 bg-gray-50 px-3 py-1.5 border border-gray-200 rounded">
          {data.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}

      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
